import {useContext, useEffect, useRef, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {OrganizationRepository} from "../../repositories/OrganizationRepository";
import {SignInContext} from "../../context/SignInContext";
import {Context as AppContext, ProjectRoles} from "../../context/AppContext";
import {ProjectEntity} from "../../model/ModelData";
import {v4 as uuid} from "uuid";
import "./onboarding.css";

const STEPS = [
    "Creating your account.",
    "Creating your organization.",
    "Preparing your project.",
    "Generating your agent flow.",
    "Saving your generated flow.",
    "Opening your flow editor.",
];

const STATUS_MESSAGES = [
    "Securely preparing your workspace.",
    "Setting up your organization.",
    "Saving the first version of your project.",
    "Your prompt is being turned into a working flow. This can take a moment.",
    "Applying the generated flow to your project.",
    "Everything is ready. Opening the flow editor now.",
];
const FRIENDLY_ERROR_MESSAGES = {
    setup: "We couldn't complete the setup. Please try again.",
    organization: "We couldn't create your organization. Please try again.",
    project: "We couldn't create your project. Please try again.",
    generation: "We couldn't generate your AI agent. Please try again.",
    save: "We couldn't save your generated flow. Please try again.",
    publish: "We couldn't publish your flow. Please try again.",
};


const collectFlowIds = (value, ids = []) => {
    if (!value || typeof value !== "object") return ids;

    if (Array.isArray(value)) {
        value.forEach((item) => collectFlowIds(item, ids));
        return ids;
    }

    Object.entries(value).forEach(([key, nestedValue]) => {
        if (key === "id" && typeof nestedValue === "string" && nestedValue) {
            ids.push(nestedValue);
        } else if (key !== "data") {
            collectFlowIds(nestedValue, ids);
        }
    });

    return ids;
};

const mergeCollection = (current = [], generated = []) => {
    const merged = [...(Array.isArray(current) ? current : [])];
    const indexes = new Map();

    merged.forEach((item, index) => {
        const key = item?.id || item?.name;
        if (key) indexes.set(key, index);
    });

    (Array.isArray(generated) ? generated : []).forEach((item) => {
        const key = item?.id || item?.name;
        if (!key) {
            merged.push(item);
            return;
        }

        if (indexes.has(key)) {
            merged[indexes.get(key)] = {...merged[indexes.get(key)], ...item};
        } else {
            indexes.set(key, merged.length);
            merged.push(item);
        }
    });

    return merged;
};

const mergeNamedCollection = (current = [], generated = []) => {
    const merged = [...(Array.isArray(current) ? current : [])];
    const indexes = new Map();

    merged.forEach((item, index) => {
        if (item?.name) indexes.set(item.name, index);
    });

    (Array.isArray(generated) ? generated : []).forEach((item) => {
        if (!item?.name) {
            merged.push(item);
            return;
        }

        if (indexes.has(item.name)) {
            const index = indexes.get(item.name);
            merged[index] = {...merged[index], ...item};
        } else {
            indexes.set(item.name, merged.length);
            merged.push(item);
        }
    });

    return merged;
};

const toCollection = (value) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === "object") return Object.values(value);
    return [];
};

const mergeRecord = (current = {}, generated = {}) => ({
    ...(current && typeof current === "object" && !Array.isArray(current) ? current : {}),
    ...(generated && typeof generated === "object" && !Array.isArray(generated) ? generated : {}),
});

const mergeGeneratedProject = (project, responseData) => {
    const generatedRoot = responseData?.project || responseData?.data?.project || responseData?.data || responseData;
    const generatedFlow = generatedRoot?.flow
        || responseData?.flow
        || (Array.isArray(responseData?.nodes) ? responseData : null);
    const generated = generatedRoot && typeof generatedRoot === "object" ? generatedRoot : {};
    const mergedProject = {...project};
    const resourceSources = generatedFlow && generatedFlow !== generated
        ? [generated, generatedFlow]
        : [generated];

    ["intents", "entities", "constants", "variables"].forEach((key) => {
        const generatedValues = resourceSources
            .map((source) => source?.[key])
            .filter((value) => value !== undefined);
        if (generatedValues.length === 0) return;

        if (key === "constants" || key === "variables") {
            mergedProject[key] = generatedValues.reduce((current, generatedValue) => (
                Array.isArray(generatedValue)
                    ? mergeCollection(current, generatedValue)
                    : mergeRecord(current, generatedValue)
            ), project[key]);
        } else {
            mergedProject[key] = generatedValues.reduce((current, generatedValue) => {
                const collection = toCollection(generatedValue).map((entity) => key === "entities"
                    ? (() => {
                        const entityType = entity?.entity_type || entity?.entityType || entity?.type || "FreeTextEntity";
                        return {
                            ...entity,
                            ...(entityType ? {
                                entity_type: entityType,
                                // Compatibility with the currently deployed Firebase mapper.
                                entityType: entityType,
                            } : {}),
                        };
                    })()
                    : entity);
                return mergeNamedCollection(current, collection);
            }, project[key]);
        }
    });

    if (generatedFlow) {
        const currentFlow = project.flows?.[0];
        const normalizedFlow = {
            ...generatedFlow,
            id: generatedFlow.id || currentFlow?.id || uuid(),
            entryNode: generatedFlow.entryNode || generatedFlow["entry-node"] || currentFlow?.entryNode,
        };

        delete normalizedFlow["entry-node"];
        mergedProject.flows = mergeCollection(project.flows, [normalizedFlow]);

        if (normalizedFlow.entryNode) {
            mergedProject.entryNode = normalizedFlow.entryNode;
        }
    }

    return mergedProject;
};

export const OnboardingPage = () => {
    const {currentUser} = useContext(SignInContext);
    const {createProject, updateProject, publishVersion} = useContext(AppContext);
    const navigate = useNavigate();
    const started = useRef(false);
    const [step, setStep] = useState(0);
    const [error, setError] = useState("");
    const baseUrl = process.env.REACT_APP_AI_FLOW_BUILDER;

    useEffect(() => {
        if (!currentUser || started.current) return;

        // Onboarding is only reachable via the landing "Generate agent" button, which stashes the
        // prompt + language in sessionStorage. If they're missing (direct URL, refresh, or Back
        // after a completed run already consumed them), don't re-run it — that would create a
        // duplicate project. Send the user to their projects instead.
        const hasPrompt = sessionStorage.getItem("openbrainAgentPrompt");
        const hasLanguage = sessionStorage.getItem("openbrainAgentLanguage");
        if (!hasPrompt || !hasLanguage) {
            navigate("/projects", {replace: true});
            return;
        }

        started.current = true;

        const runOnboarding = async () => {
            let failedOperation = "setup";
            try {
                const prompt = sessionStorage.getItem("openbrainAgentPrompt") || "";
                const storedLanguage = sessionStorage.getItem("openbrainAgentLanguage");

                let language;

                try {
                    language = storedLanguage ? JSON.parse(storedLanguage) : null;
                } catch {
                    throw new Error("Selected language data is invalid.");
                }

                if (!language?.id || !language?.code || !language?.lang) {
                    throw new Error("No valid language was selected.");
                }
                const redirect_message = sessionStorage.getItem("redirect_message");
                const ownerName = [currentUser.name, currentUser.lastname || currentUser.lastName]
                    .filter(Boolean)
                    .join(" ") || "My";
                failedOperation = "organization";

                setStep(1);
                const organizationResponse = await OrganizationRepository.create({
                    name: `${ownerName}'s Organization`,
                    organization_type: "CUSTOMER",
                    members: [currentUser.username],
                    published: false,
                });

                const organizationId = organizationResponse?.data?.id;
                if (!organizationId) throw new Error("Organization could not be created");

                setStep(2);
                const projectId = uuid();
                const configuration = {
                    "mcp_registry": "https://client.mcp.zmc.openbrain.io/",
                    "max_calls_by_user": null,
                    "conversation_logger_api": "https://api.vb.zmc.openbrain.io",
                    "is_live": true,
                    "chatbot_api": "https://stage.core.zmc.openbrain.io/api/receive"
                };
                const project = {
                    ...ProjectEntity,
                    id: projectId,
                    name: `${currentUser.name}'s AI agent`,
                    is_demo: true,
                    redirect_message: redirect_message,
                    description: prompt,
                    configuration: configuration,
                    team: [currentUser.username],
                    org_id: organizationId,
                    members: [{username: currentUser.username, role: ProjectRoles.ADMIN, vdn: ""}],
                    settings: {
                        ...ProjectEntity.settings,
                        speechToText: 51,
                        status: "BUILDING_SCENARIOS",
                        mode: "stage",
                        organization: organizationId,
                        language: language.id,
                        lang: language.lang,
                        org_id: organizationId,
                        projectType: "VOICE_BOT",
                    },
                };
                failedOperation="project";
                setStep(2);
                await createProject(project);
                failedOperation="generation";
                setStep(3);

                const flowGenerationPayload = {
                    message: prompt,
                    language: language.lang,
                    known_intent_ids: (project.intents || [])
                        .map((intent) => intent?.id)
                        .filter(Boolean),
                    existing_ids: [...new Set(collectFlowIds(project.flows || []))],
                };

                const response = await fetch(baseUrl, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${localStorage.getItem("token")}`,
                    },
                    body: JSON.stringify(flowGenerationPayload),
                });

                const responseText = await response.text();

                let responseData;
                try {
                    responseData = JSON.parse(responseText);
                } catch {
                    throw new Error("Flow generation returned an invalid response.");
                }

                if (!response.ok) {
                    throw new Error(
                        responseData?.detail ||
                        responseData?.message ||
                        `Flow generation failed with status ${response.status}.`
                    );
                }

                if (!responseData || typeof responseData !== "object") {
                    throw new Error("Flow generation returned empty or invalid data.");
                }

                const mergedProject = mergeGeneratedProject(project, responseData);
                failedOperation="save";
                setStep(4);
                await updateProject(mergedProject);
                failedOperation="publish";
                setStep(5);
                await publishVersion(mergedProject);

                sessionStorage.removeItem("openbrainAgentPrompt");
                sessionStorage.removeItem("openbrainAgentLanguage");
                sessionStorage.removeItem("redirect_message");

                setStep(STEPS.length - 1);
                setTimeout(() => navigate(`/project/${projectId}/flow`, {replace: true}), 700);
            } catch (onboardingError) {
                console.error("Onboarding failed:", {
                    operation: failedOperation,
                    error: onboardingError,
                });

                setError(
                    FRIENDLY_ERROR_MESSAGES[failedOperation] ||
                    FRIENDLY_ERROR_MESSAGES.setup
                );}
        };
        runOnboarding();
    }, [currentUser, createProject, updateProject, navigate, publishVersion, baseUrl]);

    const total = STEPS.length;
    const isComplete = step >= total - 1;
    const currentNumber = Math.min(step + 1, total);
    const progress = Math.round((step / (total - 1)) * 100);

    return (
        <Box className="onboarding-page" sx={(theme) => ({
            "--onboarding-primary": theme.palette.primary.main,
            "--onboarding-primary-dark": theme.palette.primary.dark || theme.palette.primary.main,
            "--onboarding-background": theme.palette.background1,
            "--onboarding-surface": theme.palette.white,
            "--onboarding-text": theme.palette.text.primary,
            "--onboarding-muted": theme.palette.text.secondary,
            "--onboarding-success": theme.palette.success.main,
            "--onboarding-error": theme.palette.error.main,
        })}>
            <Box className="onboarding-card">
                <Box className="onboarding-orb" aria-hidden="true"/>
                <Typography className="onboarding-eyebrow">OPENBRAIN SETUP</Typography>
                <Typography component="h1" className="onboarding-title">Your workspace is<br/>coming
                    together.</Typography>
                <Typography className="onboarding-description">We’re preparing your organization and agent flow. This
                    will only take a moment.</Typography>

                <Box className="onboarding-progress-head">
                    <span className="onboarding-progress-count">Step {currentNumber} of {total}</span>
                    <span className="onboarding-progress-pct">{progress}%</span>
                </Box>
                <Box
                    className="onboarding-progress"
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Onboarding progress"
                >
                    <Box className={`onboarding-progress-fill ${isComplete ? "is-complete" : ""}`}
                         style={{width: `${progress}%`}}/>
                </Box>

                <Box className="onboarding-steps" role="list">
                    {STEPS.map((label, index) => {
                        const state = index < step ? "completed" : index === step ? "active" : "upcoming";
                        return (
                            <Box key={label} className={`onboarding-step ${state}`} role="listitem">
                                <Box className="onboarding-step-indicator" aria-hidden="true">
                                    {state === "completed" ? (
                                        <span className="onboarding-check">✓</span>
                                    ) : state === "active" ? (
                                        <CircularProgress size={16} thickness={5} className="onboarding-spinner"/>
                                    ) : (
                                        index + 1
                                    )}
                                </Box>
                                <Box className="onboarding-step-text">
                                    <Typography className="onboarding-step-label">{label}</Typography>
                                    {state === "active" && (
                                        <Typography className="onboarding-step-hint">
                                            {STATUS_MESSAGES[Math.min(step, STATUS_MESSAGES.length - 1)]}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {error && <Typography className="onboarding-error">{error}</Typography>}
            </Box>
        </Box>
    );
};

export default OnboardingPage;
